/**
 * Catálogo de visualizaciones y configuración por defecto del cuadro de mando.
 *
 * Un widget es un objeto plano y serializable, por lo que la configuración
 * completa del informe puede guardarse en base de datos o exportarse tal cual.
 */

export const CHART_TYPES = [
  {
    value: 'kpi',
    label: 'Indicador (KPI)',
    hint: 'Un único valor agregado, con desglose opcional',
    needsDimension: false,
    allowsBreakdown: false,
  },
  { value: 'bar', label: 'Barras verticales', hint: 'Comparar categorías', needsDimension: true, allowsBreakdown: true },
  { value: 'barStacked', label: 'Barras apiladas', hint: 'Composición por categoría', needsDimension: true, allowsBreakdown: true },
  { value: 'barHorizontal', label: 'Barras horizontales', hint: 'Etiquetas largas o rankings', needsDimension: true, allowsBreakdown: true },
  { value: 'line', label: 'Líneas', hint: 'Evolución temporal', needsDimension: true, allowsBreakdown: true, temporal: true },
  { value: 'area', label: 'Áreas apiladas', hint: 'Evolución y composición', needsDimension: true, allowsBreakdown: true, temporal: true },
  { value: 'donut', label: 'Donut', hint: 'Reparto sobre el total', needsDimension: true, allowsBreakdown: false },
  { value: 'pie', label: 'Tarta', hint: 'Reparto sobre el total', needsDimension: true, allowsBreakdown: false },
  { value: 'ranking', label: 'Tabla / ranking', hint: 'Valores exactos ordenados', needsDimension: true, allowsBreakdown: false },
]

export function chartTypeMeta(value) {
  return CHART_TYPES.find((type) => type.value === value) || CHART_TYPES[1]
}

/**
 * Ajusta la configuración al cambiar de tipo de gráfico para que el resultado
 * siga teniendo sentido: los temporales pasan a un eje de fecha en orden
 * cronológico, los de reparto pierden el desglose y el KPI la dimensión.
 */
export function adaptWidgetToChartType(widget, chartType, { dateFieldKey } = {}) {
  const meta = chartTypeMeta(chartType)
  const previous = chartTypeMeta(widget.chartType)
  const next = { ...widget, chartType }

  if (!meta.needsDimension) {
    next.dimension = ''
  } else if (!widget.dimension && dateFieldKey && !next.secondMetric) {
    next.dimension = meta.temporal ? dateFieldKey : 'residuo'
  }

  // Solo se fuerza el eje temporal al entrar en un tipo temporal desde otro
  // que no lo era, para no sobrescribir una elección deliberada del usuario.
  if (meta.temporal && !previous.temporal && dateFieldKey) {
    next.dimension = dateFieldKey
    next.sort = 'natural'
    next.limit = 0
  }

  if (!meta.allowsBreakdown) next.breakdown = ''
  if (!meta.needsDimension) next.secondMetric = null
  return next
}

export const SORT_OPTIONS = [
  { value: 'valueDesc', label: 'Valor (mayor a menor)' },
  { value: 'valueAsc', label: 'Valor (menor a mayor)' },
  { value: 'labelAsc', label: 'Etiqueta (A-Z)' },
  { value: 'labelDesc', label: 'Etiqueta (Z-A)' },
  { value: 'natural', label: 'Orden natural / cronológico' },
]

export const SPAN_OPTIONS = [
  { value: 1, label: '1/3' },
  { value: 2, label: '2/3' },
  { value: 3, label: 'Completo' },
]

export const HEIGHT_OPTIONS = [
  { value: 'sm', label: 'Baja' },
  { value: 'md', label: 'Media' },
  { value: 'lg', label: 'Alta' },
]

export const HEIGHT_PX = { sm: 220, md: 300, lg: 400 }

let widgetSeq = 0
export function nextWidgetId() {
  widgetSeq += 1
  return `w-${Date.now().toString(36)}-${widgetSeq}`
}

export function createWidget(overrides = {}) {
  return {
    id: nextWidgetId(),
    title: 'Nuevo gráfico',
    chartType: 'bar',
    dimension: 'residuo',
    dateGranularity: 'month',
    breakdown: '',
    metric: { agg: 'sum', field: 'peso' },
    secondMetric: null,
    sort: 'valueDesc',
    limit: 8,
    groupOthers: false,
    span: 1,
    height: 'md',
    showLegend: true,
    compact: false,
    filters: { logic: 'AND', conditions: [] },
    ...overrides,
  }
}

/** Cuadro de mando inicial: una vista útil desde el primer render. */
export function defaultWidgets() {
  return [
    /*
    createWidget({
      title: 'Total generado',
      subtitle: 'Cantidad total de residuos',
      chartType: 'kpi',
      metric: { agg: 'sum', field: 'peso' },
      dimension: '',
      compact: true,
      span: 1,
      height: 'sm',
    }),
    createWidget({
      title: 'Valorización media',
      subtitle: 'Porcentaje medio valorizado',
      chartType: 'kpi',
      metric: { agg: 'avg', field: 'porcentajeValorizacion' },
      dimension: '',
      span: 1,
      height: 'sm',
    }),
    createWidget({
      title: 'Coste total de gestión',
      subtitle: 'Gestión + transporte',
      chartType: 'kpi',
      metric: { agg: 'sum', field: 'costeTotal' },
      dimension: '',
      compact: true,
      span: 1,
      height: 'sm',
    }),
    createWidget({
      title: 'Cantidad generada por residuo',
      subtitle: 'Top 8 residuos por peso',
      chartType: 'bar',
      dimension: 'residuo',
      metric: { agg: 'sum', field: 'peso' },
      limit: 8,
      groupOthers: true,
      span: 2,
      height: 'lg',
    }),
    createWidget({
      title: 'Distribución global',
      subtitle: 'Valorización vs. eliminación',
      chartType: 'donut',
      dimension: 'tipoGestion',
      metric: { agg: 'sum', field: 'peso' },
      limit: 0,
      span: 1,
      height: 'lg',
    }),
    createWidget({
      title: 'Evolución mensual por tipo de gestión',
      chartType: 'area',
      dimension: 'fechaTransporte',
      dateGranularity: 'month',
      breakdown: 'tipoGestion',
      metric: { agg: 'sum', field: 'peso' },
      sort: 'natural',
      limit: 0,
      span: 2,
      height: 'md',
    }),
    createWidget({
      title: 'Coste por comunidad',
      chartType: 'barHorizontal',
      dimension: 'comunidad',
      metric: { agg: 'sum', field: 'costeTotal' },
      limit: 6,
      span: 1,
      height: 'md',
    }),
    createWidget({
      title: 'Ranking de gestores finales',
      subtitle: 'Peso tratado y nº de retiradas',
      chartType: 'ranking',
      dimension: 'gestor',
      metric: { agg: 'sum', field: 'peso' },
      limit: 6,
      span: 1,
      height: 'md',
    }),
    createWidget({
      title: 'Kilómetros por transportista',
      chartType: 'barStacked',
      dimension: 'transportista',
      breakdown: 'tipoResiduo',
      metric: { agg: 'sum', field: 'km' },
      limit: 6,
      span: 2,
      height: 'md',
    }),
    */
    createWidget({
          title: 'Total costes',
          subtitle: 'Coste total de gestión',
          chartType: 'kpi',
          metric: { agg: 'sum', field: 'costeTotal' },
          dimension: '',
          compact: true,
          span: 1,
          height: 'sm',
    }),

    createWidget({
      title: 'Total generado',
      subtitle: 'Cantidad total de residuos',
      chartType: 'kpi',
      metric: { agg: 'sum', field: 'peso' },
      dimension: '',
      compact: true,
      span: 1,
      height: 'sm',
    }),

    createWidget({
      title: 'Segregación total',
      subtitle: '% segregación global',
      chartType: 'kpi',
      metric: { agg: 'avg', field: 'pctSegregacionTotal' },
      dimension: '',
      compact: true,
      span: 1,
      height: 'sm',
    }),

    createWidget({
      title: 'Cantidad generada por residuo',
      subtitle: 'Peso total vs. cantidad valorizada',
      chartType: 'bar',
      dimension: 'residuo',
      metric: { agg: 'sum', field: 'peso' },
      secondMetric: { agg: 'sum', field: 'cantidadValorizada' },
      limit: 8,
      groupOthers: true,
      span: 2,
      height: 'lg',
    }),

    createWidget({
      title: 'Distribución global',
      subtitle: 'Valorización vs. eliminación',
      chartType: 'donut',
      dimension: '',
      metric: { agg: 'sum', field: 'cantidadValorizada' },
      secondMetric: { agg: 'sum', field: 'cantidadEliminada' },
      limit: 0,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Costes por cadena',
      subtitle: 'Coste real vs. coste previsto',
      chartType: 'barHorizontal',
      dimension: 'cadena',
      metric: { agg: 'sum', field: 'costeReal' },
      secondMetric: { agg: 'sum', field: 'costePrevisto' },
      limit: 8,
      groupOthers: true,
      span: 2,
      height: 'lg',
    }),

    createWidget({
      title: 'Segregación y valorización por cadena',
      subtitle: '% valorización vs. % segregación',
      chartType: 'bar',
      dimension: 'cadena',
      metric: { agg: 'avg', field: 'pctValorizacion' },
      secondMetric: { agg: 'avg', field: 'pctSegregacion' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Segregación y valorización por país',
      subtitle: '% valorización vs. % segregación',
      chartType: 'bar',
      dimension: 'pais',
      metric: { agg: 'avg', field: 'pctValorizacionPais' },
      secondMetric: { agg: 'avg', field: 'pctSegregacionPais' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Segregación y valorización por tipo de obra',
      subtitle: '% valorización vs. % segregación',
      chartType: 'barHorizontal',
      dimension: 'tipoObra',
      metric: { agg: 'avg', field: 'pctValorizacionTipoObra' },
      secondMetric: { agg: 'avg', field: 'pctSegregacionTipoObra' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Segregación total por país',
      subtitle: '% Segregación total vs. % Mezcla',
      chartType: 'bar',
      dimension: 'pais',
      metric: { agg: 'avg', field: 'pctSegregacionTotalPais' },
      secondMetric: { agg: 'avg', field: 'pctMezclaPais' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Segregación total por tipo de obra',
      subtitle: '% Segregación total vs. % Mezcla',
      chartType: 'barHorizontal',
      dimension: 'tipoObra',
      metric: { agg: 'avg', field: 'pctSegregacionTotalTipoObra' },
      secondMetric: { agg: 'avg', field: 'pctMezclaTipoObra' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Segregación total por año',
      subtitle: '% Segregación total vs. % Mezcla',
      chartType: 'line',
      dimension: 'fechaTransporte',
      dateGranularity: 'year',
      metric: { agg: 'avg', field: 'pctSegregacionTotal' },
      secondMetric: { agg: 'avg', field: 'pctMezcla' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),
/*
   createWidget({
      title: 'Abono chatarra por obra',
      subtitle: '€ de abono de chatarra por obra',
      chartType: 'barHorizontal',
      dimension: 'obra',
      metric: { agg: 'sum', field: 'abonoChatarra' },
      limit: 15,
      span: 2,
      height: 'lg',
    }),*/

    createWidget({
      title: 'Emisiones por tipo de obra',
      subtitle: 'CO₂, CH₄ y N₂O por tipo de obra',
      chartType: 'donut',
      dimension: 'tipoObra',
      metric: { agg: 'sum', field: 'emisionesTotales' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Emisiones por cadena',
      subtitle: 'CO₂, CH₄ y N₂O por cadena',
      chartType: 'bar',
      dimension: 'cadena',
      metric: { agg: 'sum', field: 'emisionesTotales' },
      limit: 10,
      span: 2,
      height: 'lg',
    }),

    createWidget({
      title: 'Emisiones totales',
      subtitle: 'Huella de carbono total',
      chartType: 'kpi',
      metric: { agg: 'sum', field: 'emisionesTotales' },
      dimension: '',
      compact: true,
      span: 1,
      height: 'sm',
    }),
    
/*
    createWidget({
      title: 'Emisiones por país',
      subtitle: 'CO₂, CH₄ y N₂O por país',
      chartType: 'bar',
      dimension: 'pais',
      metric: { agg: 'sum', field: 'emisionesTotales' },
      limit: 10,
      span: 1,
      height: 'lg',
    }),

    createWidget({
      title: 'Emisiones por año',
      subtitle: 'CO₂, CH₄ y N₂O por año',
      chartType: 'line',
      dimension: 'fechaFin',
      dateGranularity: 'year',
      metric: { agg: 'sum', field: 'emisionesTotales' },
      span: 2,
      height: 'lg',
    }),
*/
    
  ]
}
